import { type ReactNode } from 'react';
import { Avatar } from '@/components/ui/avatar';
import { useMemberDisplay } from '@/hooks/use-member-display';
import { cn } from '@/utils/cn';
import { pbFileUrl } from '@/utils/pb-file-url';
import type { PodiumRowData } from '../utils/bonus-indicators';

const POS_TONE = [
  'text-brand-red',
  'text-text-secondary',
  'text-brand-coral',
] as const;

function posCls(pos: number): string {
  return POS_TONE[pos - 1] ?? 'text-text-tertiary';
}

export function Position({ pos }: { pos: number }): ReactNode {
  return (
    <span
      className={cn(
        'w-5 shrink-0 font-mono text-xs font-semibold tabular-nums',
        posCls(pos)
      )}
    >
      {pos}
    </span>
  );
}

function photoSrc(row: PodiumRowData): string {
  if (!row.photo || !row.collectionId || !row.collectorId) return '';
  return pbFileUrl(
    { id: row.collectorId, collectionId: row.collectionId },
    row.photo
  );
}

export function RowSubject({ row }: { row: PodiumRowData }): ReactNode {
  const display = useMemberDisplay({
    id: row.collectorId ?? '',
    ...(row.firstName && { first_name: row.firstName }),
    ...(row.photo && { photo: row.photo }),
  });
  if (row.totalLeads === null)
    return <span className="flex-1 text-[11px] text-text-tertiary">—</span>;
  return (
    <>
      <Avatar src={photoSrc(row)} name={display.name} size="xs" />
      <span className="flex-1 truncate text-[11px] text-text-secondary">
        {display.name}{' '}
        <span className="text-text-tertiary">({row.totalLeads} creadores)</span>
      </span>
    </>
  );
}

export function RowMeta({ row }: { row: PodiumRowData }): ReactNode {
  return (
    <>
      {row.isCurrent && (
        <span className="text-[9px] font-semibold uppercase tracking-wider text-brand-red">
          tú
        </span>
      )}
      <span className="font-mono text-[11px] tabular-nums text-text-tertiary">
        {row.eur ? `$${row.eur}` : '—'}
      </span>
    </>
  );
}
